import {
  Autocomplete as MantineAutocomplete,
  AutocompleteProps as MantineAutocompleteProps,
} from '@mantine/core';
import clsx from 'clsx';
import { forwardRef } from 'react';

// Reuse the v2 Select styling so the autocomplete is visually identical
// (font, label, height, dropdown, options) to the Select it sits next to.
import selectStyles from '../Select/Select.module.css';

export type AutocompleteSize = 'xs' | 'md' | 'lg';

export interface AutocompleteProps extends Omit<
  MantineAutocompleteProps,
  'variant' | 'color' | 'styles' | 'classNames' | 'style' | 'size' | 'readOnly'
> {
  /** Input size: 'xs' (24px), 'md' (40px), or 'lg' (48px). Default: 'md' */
  size?: AutocompleteSize;
  /** Add 'fs-mask' class for FullStory masking of sensitive data */
  isMasked?: boolean;
  isReadOnly?: boolean;
}

export const Autocomplete = forwardRef<HTMLInputElement, AutocompleteProps>(
  (
    { size = 'md', label, error, isMasked = false, isReadOnly, ...props },
    ref
  ) => {
    // Extract consumer's comboboxProps and wrapperProps so we can merge them
    // rather than letting the spread overwrite our dropdown/options/option
    // class names or the internal data-input-size sizing attribute.
    const {
      comboboxProps: consumerComboboxProps,
      wrapperProps: consumerWrapperProps,
      ...restProps
    } = props;

    return (
      <MantineAutocomplete
        ref={ref}
        unstyled
        classNames={{
          root: selectStyles.root,
          wrapper: selectStyles.wrapper,
          input: clsx(selectStyles.input, isMasked && 'fs-mask'),
          section: selectStyles.section,
          label: selectStyles.label,
          error: selectStyles.error,
          required: selectStyles.required,
          description: selectStyles.description,
        }}
        comboboxProps={{
          ...consumerComboboxProps,
          // Dropdown is portaled outside the component tree, so its classes
          // must be passed via comboboxProps rather than top-level classNames.
          classNames: {
            dropdown: selectStyles.dropdown,
            options: selectStyles.options,
            option: selectStyles.option,
            ...consumerComboboxProps?.classNames,
          },
        }}
        wrapperProps={{ ...consumerWrapperProps, 'data-input-size': size }}
        label={label}
        error={error}
        readOnly={isReadOnly}
        {...restProps}
      />
    );
  }
);

Autocomplete.displayName = 'Autocomplete';
